import { onMounted, onUnmounted, readonly, ref } from 'vue'

declare global {
  interface Window {
    JSBridge?: {
      postMessage: (message: string) => void
      onmessage: ((event: MessageEvent) => void) | null
    }
  }
}

type IncomingMessage = {
  version: number
  action: string
  detail: string | Record<string, unknown>
}

const JS_BRIDGE_VERSION = 1

export function useJSBridge() {
  const receivedEvent = ref('')
  const messageHandlers = new Map<
    string,
    (detail: IncomingMessage['detail']) => void
  >()

  function handleIncomingMessage(event: MessageEvent) {
    let parsed: IncomingMessage
    try {
      parsed =
        typeof event.data === 'string' ? JSON.parse(event.data) : event.data
    } catch {
      return
    }

    const { version, action, detail } = parsed

    if (version !== JS_BRIDGE_VERSION) {
      return
    }

    messageHandlers.get(action)?.(detail)
  }

  onMounted(() => {
    if (!window.JSBridge) return

    messageHandlers.set('chat', (detail) => {
      receivedEvent.value =
        typeof detail === 'string' ? detail : JSON.stringify(detail)
    })

    window.JSBridge.onmessage = handleIncomingMessage
  })

  onUnmounted(() => {
    if (window.JSBridge) {
      window.JSBridge.onmessage = null
    }
  })

  function clearReceivedEvent() {
    receivedEvent.value = ''
  }

  function postMessage(
    payload: { version: number; action: string } & Record<string, unknown>,
  ) {
    if (!window.JSBridge) {
      return
    }
    window.JSBridge.postMessage(JSON.stringify(payload))
  }

  function sendReady() {
    postMessage({ version: JS_BRIDGE_VERSION, action: 'ready' })
  }

  function closeWebview() {
    postMessage({ version: JS_BRIDGE_VERSION, action: 'close' })
  }

  function showDialog({
    title,
    description,
    acceptButtonText,
    declineButtonText,
    onAccept,
    onDecline,
  }: {
    title: string
    description: string
    acceptButtonText: string
    declineButtonText: string
    onAccept: () => void
    onDecline?: () => void
  }) {
    messageHandlers.set('dialog', (detail) => {
      if (detail === 'positive') onAccept()
      if (detail === 'negative') onDecline?.()
    })

    postMessage({
      version: JS_BRIDGE_VERSION,
      action: 'showAlert',
      title,
      description,
      positiveButtonText: acceptButtonText,
      negativeButtonText: declineButtonText,
    })
  }

  function addToCart() {
    postMessage({
      version: JS_BRIDGE_VERSION,
      action: 'cartUpdated',
    })
  }

  return {
    closeWebview,
    showDialog,
    sendReady,
    addToCart,
    clearReceivedEvent,
    receivedEvent: readonly(receivedEvent),
  }
}
